import React from "react";
import { WebSocket as ReconnectingWebSocket } from "partysocket";
import { WS_URL } from "../config";
import {
  machineFromSnapshot,
  machineApply,
  machineUndo,
} from "../engine/gameMachine";
import { fetchFinalBoard } from "../components/HistoryWindow/historyApi";
import { screensaverIdleMs } from "../components/Screensaver/Screensaver";

// Identity lives in localStorage: one person per browser, surviving
// restarts — a tab close within the idle window resumes the game, and
// reopening the site can't mint zombie duplicate games. `?session`
// switches to per-tab sessionStorage identity for multi-client testing
// (each tab with the flag is its own user).
const UUID_KEY = "saat.uuid";
const GAME_KEY = "saat.gameId";

// Storage access itself can throw (Safari lockdown modes, blocked site
// data) — fall back to an in-memory store so identity is session-lifetime
// rather than the app dying at module load.
const memoryStore = (() => {
  const m = new Map();
  return {
    getItem: (k) => (m.has(k) ? m.get(k) : null),
    setItem: (k, v) => m.set(k, String(v)),
    removeItem: (k) => m.delete(k),
  };
})();

const identityStore = (() => {
  try {
    const store = new URLSearchParams(
      typeof window === "undefined" ? "" : window.location.search,
    ).has("session")
      ? window.sessionStorage
      : window.localStorage;
    // Verify it actually works — Safari private mode historically
    // exposed a store whose setItem throws.
    store.setItem("saat.probe", "1");
    store.removeItem("saat.probe");
    return store;
  } catch {
    return memoryStore;
  }
})();
// Final boards are heavier than roster IDs; bound the cache so a
// browser left open for hours doesn't turn into an unbounded archive.
const MAX_BROWSE_FINAL_STATES = 64;

function makeUuid() {
  // crypto.randomUUID needs a secure context; phone-on-LAN dev testing
  // over plain http doesn't have one. Same bytes, hand-formatted.
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function getOrCreateUuid() {
  let uuid = identityStore.getItem(UUID_KEY);
  if (!uuid) {
    uuid = makeUuid();
    try {
      identityStore.setItem(UUID_KEY, uuid);
    } catch {
      // Quota/blocked mid-session: keep the in-memory uuid for this run.
    }
  }
  return uuid;
}

function getStoredGameId() {
  const raw = identityStore.getItem(GAME_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function storeGameId(n) {
  try {
    if (n == null) identityStore.removeItem(GAME_KEY);
    else identityStore.setItem(GAME_KEY, String(n));
  } catch {
    // Quota/blocked mid-session: resume-after-reload degrades, play doesn't.
  }
}

// Dev affordance: `?seed=42` forces the client to request game_id=42,
// overriding identity storage. Needs -dev-create server-side (off by
// default — see server/scripts/server.sh).
function readSeedOverride() {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("seed");
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

// Dev knob: fake a neighbor's ending ~6s after the room loads,
// client-side only, so the departure ceremonies can be eyeballed on
// demand. `?ritual` → close ritual (hand walks to the X, presses it,
// window closes); `?spectatewin` → spectated win cascade. The server
// still thinks the game is live, so no reseat follows — the seat stays
// empty until reload (use ?devwin / bots for a real ending + redeal).
function readSpectateKnob() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.has("spectatewin")) return "won";
  if (params.has("ritual")) return "abandoned";
  return null;
}

export default function useSolitaireServer({ neighborCount = 6 } = {}) {
  const boundedNeighborCount = Math.max(0, Math.min(8, neighborCount));
  const neighborCountRef = React.useRef(boundedNeighborCount);
  neighborCountRef.current = boundedNeighborCount;
  // The last value included in hello or acknowledged by a welcome. This
  // prevents the post-welcome render from redundantly setting the count.
  const sentNeighborCountRef = React.useRef(null);
  const [myGameId, setMyGameId] = React.useState(null);
  // Move history the server has for our own game, as of the latest
  // welcome/rejection. PlayableSolitaire reads this once in its lazy
  // state init to resume after a refresh, then owns state from there —
  // later changes to this value are ignored by design.
  const [myGameMoves, setMyGameMoves] = React.useState(null);
  // Whether undo is armed for the resumed history (the server's view;
  // snapshots are self-contained so a refresh mid-game keeps its undo).
  const [myGameCanUndo, setMyGameCanUndo] = React.useState(false);
  const [activeGameIds, setActiveGameIds] = React.useState([]);
  // The server's ordered watched set is deliberately separate from all
  // loaded machines. Browse snapshots must not quietly rearrange the
  // desktop behind the containing window.
  const [watchedGameIds, setWatchedGameIds] = React.useState([]);
  // One entry per watched seat: gameId -> phase. Key membership IS the
  // watched set (insertion order mirrors the server's ordered list);
  // the phase tracks a seat's ending ceremony:
  //   "live"     — normal spectated window.
  //   "ended"    — game_ended arrived; the live subscription is closed
  //                and the window is playing its departure (close
  //                ritual / cascade).
  //   "departed" — the ceremony finished: the window is gone from the
  //                desktop, but the id stays here (and in reconnect
  //                re-asserts) until the server's reseat welcome swaps
  //                a fresh game into the slot — the gap is the
  //                deliberate "someone left the table" beat.
  const seatPhasesRef = React.useRef(new Map());
  // True once any welcome has landed: distinguishes the first-ever hello
  // (server seeds the room) from a reconnect (client re-asserts its room).
  const hadWelcomeRef = React.useRef(false);
  // All Active Games is a cheap ordered roster plus a bounded subset of
  // live machines. Ended IDs remain in the roster as stable stubs until
  // the containing window closes.
  const [browseGameIds, setBrowseGameIds] = React.useState([]);
  const [browseEndedIds, setBrowseEndedIds] = React.useState(
    () => new Set(),
  );
  // Published mirror of the seats whose phase is "departed" (App hides
  // those windows while their seat waits for the reseat).
  const [departedWatchedIds, setDepartedWatchedIds] = React.useState(
    () => new Set(),
  );
  // Ended browse stubs get their final board over HTTP (the Past Games
  // endpoint) — in-flight ids so a stub isn't fetched twice, and a ref
  // into the socket effect's rememberBrowseFinal.
  const finalFetchesRef = React.useRef(new Set());
  const rememberFinalRef = React.useRef(() => {});
  // Set on socket open, consumed by the first welcome: distinguishes a
  // reconnect welcome (rebuild the browse roster, quiet arrivals) from
  // mid-session welcomes.
  const freshSocketRef = React.useRef(false);
  // Ids whose windows arrived via a RECONNECT welcome: they open with
  // no animation — waking from the screensaver (or a laptop lid)
  // presents the settled room, not a parade of arrivals. Entries clear
  // when the machine dies. The session's very first welcome is NOT a
  // reconnect — it's the seeding, and seeding gets the arrival
  // choreography.
  const everWelcomedRef = React.useRef(false);
  const quietArrivalIdsRef = React.useRef(new Set());
  const [quietArrivalIds, setQuietArrivalIds] = React.useState(
    () => new Set(),
  );
  // Machine identity generation per game id: bumped whenever a snapshot
  // creates a machine from scratch (vs. replacing a live one). Windows key
  // their subscription on it so a rebuilt id gets a fresh, un-frozen view.
  const machineGenRef = React.useRef(new Map());
  const [gameGenerations, setGameGenerations] = React.useState(
    () => new Map(),
  );
  // A cached machine is not necessarily current after it left the
  // viewport. Only a fresh game_present for the current browse_set makes
  // a preview ready; until then the UI keeps its dim empty board.
  const [browseReadyIds, setBrowseReadyIds] = React.useState(
    () => new Set(),
  );
  // Final boards for games that ended while in the bounded browse range —
  // captured from the live machine before it's released. Capped
  // independently and cleared with the browse roster.
  const [browseFinalStates, setBrowseFinalStates] = React.useState(
    () => new Map(),
  );
  const browseWantedRef = React.useRef(false);
  const browseRequestedRef = React.useRef(new Set());
  // Ended stubs leave the browse grid after a short linger — mobile
  // keeps its window open indefinitely, so dead boards must not
  // accumulate. id → removal timer; cancelled when a ghost replay
  // re-adds the id, cleared wholesale with the roster.
  const browsePruneTimersRef = React.useRef(new Map());
  // Monotonic range generation: a snapshot from an earlier scroll must
  // never make a newly revisited slot look current.
  const browseSeqRef = React.useRef(0);
  const pruneBrowseRef = React.useRef(() => {});
  // Browse slots whose win cascade is playing: id → {timer, wonAt}.
  // Destructive bookkeeping (ended stub, cached final board, machine
  // teardown) is deferred via `timer` so the show isn't cut mid-air.
  // `wonAt` is the win epoch (Date.now domain) — since the cascade is a
  // deterministic function of (game id, elapsed time), a slot mounting
  // mid-show fast-forwards to the exact frame every other viewer is on;
  // a late joiner derives it from the server's won_elapsed_ms. Read by
  // subscribeGame's handle.
  const celebrationsRef = React.useRef(new Map());
  // Set inside the connection effect; SpectatedSolitaire reports its
  // cascade finished through the subscribe handle → this.
  const settleCelebrationRef = React.useRef(() => {});
  const browseRetryTimerRef = React.useRef(null);
  // How many games are live in the whole room right now (yours included).
  const [roomActive, setRoomActive] = React.useState(0);

  // Bumped when the server tells us our optimistic local state is wrong
  // (move_rejected, or a welcome-time consistency failure). App keys
  // PlayableSolitaire on this so the game remounts and rebuilds from the
  // server's history.
  const [myGameResyncNonce, setMyGameResyncNonce] = React.useState(0);

  // --- presence / lifecycle ---
  // Idle model: the server sweeps games with no productive move for
  // SERVER_IDLE_MS (X; mirrors idleTimeout in server/hub.go — change
  // together); the screensaver takes over at screensaverIdleMs() (Y) of
  // total input silence, Y < X. Exactly one prompt exists — the "you
  // haven't made a move" warning — shown shortly before the sweep ONLY
  // while the player is awake (input newer than Y). A player under the
  // screensaver gets no prompt; the game dies behind the pipes and they
  // meet the timed-out notice on wake. Dev knob: `?idle=SECONDS` shrinks
  // the window (pair with the server's -idle-seconds / IDLE_SECONDS,
  // and `?ss=SECONDS` for the saver).
  const idleWindowMs = React.useMemo(() => {
    if (typeof window === "undefined") return 8 * 60 * 1000;
    const raw = new URLSearchParams(window.location.search).get("idle");
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 10 ? n * 1000 : 8 * 60 * 1000;
  }, []);
  const SERVER_IDLE_MS = idleWindowMs;
  const LAST_CHANCE_LEAD_MS = Math.round(idleWindowMs * (1.5 / 8));
  const IDLE_CHECK_MS = Math.max(1000, Math.min(15000, idleWindowMs / 20));
  const AWAKE_MS = React.useMemo(() => screensaverIdleMs(), []);
  // null | { kind: "no-move", minutes }
  const [idlePrompt, setIdlePrompt] = React.useState(null);
  // True when the server abandoned OUR game out from under us (idle
  // timeout). We keep the board mounted and let the UI say so instead
  // of silently dealing a fresh game.
  const [myGameTimedOut, setMyGameTimedOut] = React.useState(false);
  const myGameTimedOutRef = React.useRef(false);
  const updateTimedOut = (v) => {
    myGameTimedOutRef.current = v;
    setMyGameTimedOut(v);
  };
  // True after our own win echoes back. The game is over but stays
  // mounted for the cascade — over means no idle prompt, no still_here,
  // and nothing to abandon when the player eventually re-deals (the
  // state copy lets the UI skip the "Abandon this game?" confirm).
  const [myGameWon, setMyGameWon] = React.useState(false);
  const myGameWonRef = React.useRef(false);
  const updateWon = (v) => {
    myGameWonRef.current = v;
    setMyGameWon(v);
  };
  // True after the socket has been down long enough that this stopped
  // being a blip (~15s). Reconnection keeps being attempted regardless;
  // this only gates whether the UI mentions it.
  const [netTrouble, setNetTrouble] = React.useState(false);
  const netTimerRef = React.useRef(null);
  const lastActivityRef = React.useRef(Date.now());
  // Any input at all — pointer motion, clicks, keys, touch. Cheap ref
  // writes; this is what distinguishes "away" from "around but not
  // moving cards" for the two prompt flavors above.
  const lastInputRef = React.useRef(Date.now());
  React.useEffect(() => {
    const mark = () => {
      lastInputRef.current = Date.now();
    };
    const opts = { capture: true, passive: true };
    const events = ["pointermove", "pointerdown", "keydown", "wheel", "touchstart"];
    for (const e of events) window.addEventListener(e, mark, opts);
    return () => {
      for (const e of events) window.removeEventListener(e, mark, opts);
    };
  }, []);

  const wsRef = React.useRef(null);
  // One-shot latch for the ?devwin verification knob.
  const devWinSentRef = React.useRef(false);
  const pendingGameIdRef = React.useRef(null);
  // True from sendHello until its welcome/error lands — guards against
  // firing a second automatic hello while one is already in flight.
  const helloInFlightRef = React.useRef(false);
  // Set inside the connection effect so UI callbacks (dealNewGame) can
  // trigger a fresh hello without owning the socket internals.
  const sendHelloRef = React.useRef(null);
  // Mirror of myGameId for reading inside message handlers without stale
  // closures. Updated anywhere we set myGameId.
  const myGameIdRef = React.useRef(null);

  // Our optimistic history length for our own game — the seq every
  // outbound move/undo is minted at. The server accepts an op only at
  // the exact position it was minted for (seq == len(server history)),
  // so a stale op is rejected with the authoritative history instead of
  // being silently reinterpreted. null until the first welcome.
  const ownSeqRef = React.useRef(null);

  // Own-game ops composed while the socket was down. Each entry is
  // {gameId, payload} (payload carries its minted seq); flushed on the
  // next welcome IF the server's history lines up exactly underneath
  // them, dropped otherwise (see the welcome consistency check).
  const outboxRef = React.useRef([]);

  // Spectated-game ordering. seqRef: gameId -> next expected seq (== number
  // of moves reduced so far). The wire guarantees per-client FIFO with
  // no holes (PROTOCOL.md §1-3), so a gap is a violation, not a race —
  // there is deliberately no out-of-order buffer.
  const seqRef = React.useRef(new Map());

  // Pure state machine per non-own active game: { board, prevBoard }.
  // Self-contained — snapshots carry can_undo, so a machine built at
  // connect time behaves identically to one that watched every move
  // (this killed the old one-deep prevState cache and its "connected
  // mid-undo-window" corruption hole). The player's own game is owned by
  // PlayableSolitaire (which applies moves optimistically); we
  // deliberately don't duplicate it here to avoid two sources of truth.
  const machinesRef = React.useRef(new Map());
  // gameId -> Set<(event) => void>. Events:
  //   { prevState, curState, move, terminal }
  // - Fired on each applied spectated move (move is the engine move object).
  // - Fired once on game end with terminal: true, move: null; the subscriber
  //   is then automatically unsubscribed.
  // - Fired on reconnect-welcome/refreshed snapshots for games still
  //   present with move: null, terminal: false — a "resync to curState"
  //   signal.
  const subscribersRef = React.useRef(new Map());

  const publishActiveGameIds = () => {
    setActiveGameIds(Array.from(machinesRef.current.keys()));
    setWatchedGameIds(Array.from(seatPhasesRef.current.keys()));
    const departed = new Set();
    for (const [id, phase] of seatPhasesRef.current) {
      if (phase === "departed") departed.add(id);
    }
    setDepartedWatchedIds(departed);
    setQuietArrivalIds(new Set(quietArrivalIdsRef.current));
    setGameGenerations(new Map(machineGenRef.current));
  };

  const updateMyGameId = React.useCallback((nextId) => {
    myGameIdRef.current = nextId;
    setMyGameId(nextId);
    storeGameId(nextId);
  }, []);

  // Dev-knob state (see readSpectateKnob): fire at most once per load.
  const knobFiredRef = React.useRef(false);

  React.useEffect(() => {
    const uuid = getOrCreateUuid();
    const spectateKnob = readSpectateKnob();
    const ws = new ReconnectingWebSocket(WS_URL);
    wsRef.current = ws;

    const sendHello = (gameId, { fresh = false } = {}) => {
      const msg = {
        type: "hello",
        user_uuid: uuid,
        neighbor_count: neighborCountRef.current,
      };
      // Reconnect re-asserts the room verbatim (ordered, including frozen
      // ended windows and deliberate vacancies) instead of letting the
      // server deal a fresh seed — a wifi blip must not replace the
      // player's chosen neighbors. Absent on the first-ever hello so the
      // server's one-time seed still happens.
      if (hadWelcomeRef.current) {
        msg.watched_game_ids = Array.from(seatPhasesRef.current.keys());
      }
      if (gameId != null) msg.game_id = gameId;
      // fresh: abandon whatever game this session owns and deal a new
      // one, in ONE op — replaces the old abandon-then-hello pair, which
      // could race/interleave across a reconnect.
      if (fresh) msg.fresh = true;
      pendingGameIdRef.current = gameId ?? null;
      helloInFlightRef.current = true;
      sentNeighborCountRef.current = neighborCountRef.current;
      ws.send(JSON.stringify(msg));
    };
    sendHelloRef.current = sendHello;

    const onOpen = () => {
      if (netTimerRef.current) {
        clearTimeout(netTimerRef.current);
        netTimerRef.current = null;
      }
      // The next welcome answers a fresh socket: the server-side Client
      // is new, so the browse roster (if open) must be re-established.
      // Ordinary mid-session welcomes (watch/unwatch/reseat) must NOT
      // rebuild it — that would evict ended stubs and drop scroll state.
      freshSocketRef.current = true;
      setNetTrouble(false);
      const override = readSeedOverride();
      sendHello(override ?? getStoredGameId());
    };
    const onClose = () => {
      if (browseRetryTimerRef.current != null) {
        clearTimeout(browseRetryTimerRef.current);
        browseRetryTimerRef.current = null;
      }
      // Blips stay silent; only sustained failure gets a dialog. Null
      // the ref when the timer fires so the NEXT close can re-arm it —
      // otherwise dismissing the notice mid-outage silences it for good.
      if (!netTimerRef.current) {
        netTimerRef.current = setTimeout(() => {
          netTimerRef.current = null;
          setNetTrouble(true);
        }, 15000);
      }
    };

    const notifySubscribers = (gameId, event) => {
      const subs = subscribersRef.current.get(gameId);
      if (!subs) return;
      for (const cb of subs) cb(event);
    };

    rememberFinalRef.current = (gameId, board) =>
      rememberBrowseFinal(gameId, board);
    const rememberBrowseFinal = (gameId, board) => {
      const finalBoard = board ?? machinesRef.current.get(gameId)?.board;
      if (!finalBoard) return;
      setBrowseFinalStates((prev) => {
        const next = new Map(prev);
        next.delete(gameId);
        next.set(gameId, finalBoard);
        while (next.size > MAX_BROWSE_FINAL_STATES) {
          next.delete(next.keys().next().value);
        }
        return next;
      });
    };

    // An ended stub lingers long enough to read the ending, then its
    // slot leaves the grid entirely (wins spend their whole cascade
    // live first — browse_ended is deferred past the celebration).
    // Removing the id shrinks renderedIds, so the visible-range effect
    // follows with a browse_set that drops the subscription too.
    const BROWSE_ENDED_LINGER_MS = 10_000;
    const scheduleBrowsePrune = (gameId) => {
      const timers = browsePruneTimersRef.current;
      clearTimeout(timers.get(gameId));
      timers.set(
        gameId,
        setTimeout(() => {
          timers.delete(gameId);
          setBrowseGameIds((prev) => prev.filter((id) => id !== gameId));
          setBrowseEndedIds((prev) => {
            if (!prev.has(gameId)) return prev;
            const next = new Set(prev);
            next.delete(gameId);
            return next;
          });
          setBrowseFinalStates((prev) => {
            if (!prev.has(gameId)) return prev;
            const next = new Map(prev);
            next.delete(gameId);
            return next;
          });
        }, BROWSE_ENDED_LINGER_MS),
      );
    };

    // A protocol violation (bad move index, impossible undo, seq gap)
    // means this machine's stream can no longer be trusted. The
    // seq-guarded, no-holes wire makes these unreachable in theory, so
    // recovery is deliberately blunt: cycle the socket and rebuild
    // EVERYTHING from the reconnect welcome — the one resync path that
    // is exercised constantly and tested.
    const forceResync = (why) => {
      console.error(`useSolitaireServer: ${why}; cycling the socket`);
      ws.reconnect();
    };

    const endGame = (gameId, reason) => {
      const m = machinesRef.current.get(gameId);
      if (m) {
        notifySubscribers(gameId, {
          prevState: m.board,
          curState: m.board,
          move: null,
          terminal: true,
          reason: reason ?? null,
        });
      }
      subscribersRef.current.delete(gameId);
      machinesRef.current.delete(gameId);
      seqRef.current.delete(gameId);
      seatPhasesRef.current.delete(gameId);
      quietArrivalIdsRef.current.delete(gameId);
    };

    // A watched game ending starts its departure: the live subscription
    // is finished, and the machine + watched-set membership stay while
    // the window plays the close ritual (or the cascade). The window
    // reports departWatchedGame when the ceremony ends; the id then
    // waits invisibly for the server's reseat welcome to clear it.
    const finishWatchedGame = (gameId, reason) => {
      const m = machinesRef.current.get(gameId);
      if (!m) return;
      notifySubscribers(gameId, {
        prevState: m.board,
        curState: m.board,
        move: null,
        terminal: true,
        reason: reason ?? null,
      });
      subscribersRef.current.delete(gameId);
      if (seatPhasesRef.current.has(gameId)) {
        seatPhasesRef.current.set(gameId, "ended");
      }
    };

    // A browse-grid neighbor WON while on screen (the server sends the
    // reasoned game_ended to browse-subscribed clients): play their
    // cascade in the slot instead of snapping to a frozen board — the
    // piece's one gift, witnessed from the chaos window too. The live
    // stream is over, but the machine and the slot's ready state stay
    // so the SpectatedSolitaire instance survives to run the show. The
    // deferred bookkeeping lands when the window reports its cascade
    // done, when the fallback timer fires, or when a scroll-away prune
    // unmounts the slot — whichever comes first.
    const CASCADE_GRACE_MS = 4 * 60 * 1000; // a full 52-card run is minutes
    const settleBrowseCelebration = (gameId) => {
      const show = celebrationsRef.current.get(gameId);
      if (show == null) return;
      clearTimeout(show.timer);
      celebrationsRef.current.delete(gameId);
      // Final board first — endGame deletes the machine it reads.
      setBrowseEndedIds((prev) => new Set(prev).add(gameId));
      rememberBrowseFinal(gameId);
      setBrowseReadyIds((prev) => {
        const next = new Set(prev);
        next.delete(gameId);
        return next;
      });
      // A watched winner keeps its machine — the frozen desktop window
      // owns it until X/replacement. Only browse-only machines tear down.
      if (!seatPhasesRef.current.has(gameId)) endGame(gameId);
      scheduleBrowsePrune(gameId);
      publishActiveGameIds();
    };
    settleCelebrationRef.current = settleBrowseCelebration;

    // notify: false when finishWatchedGame delivers the terminal event
    // itself (a second one would be ignored, but one is cleaner).
    // wonElapsedMs > 0 means we JOINED a show already in flight (the
    // server's celebrating-stub snapshot said how far in) — the win
    // epoch and the settle fallback both shift back by that much.
    const beginBrowseCelebration = (gameId, { notify, wonElapsedMs = 0 }) => {
      const m = machinesRef.current.get(gameId);
      if (!m) return;
      // The same server beat ships browse_ended BEFORE this reasoned
      // game_ended (one coalesced frame), so the ended-stub bookkeeping
      // has already landed by the time we know it's a win. Reverse it
      // in the same React batch: the slot stays live for the show, and
      // the settle re-stamps everything when the cascade ends.
      setBrowseEndedIds((prev) => {
        if (!prev.has(gameId)) return prev;
        const next = new Set(prev);
        next.delete(gameId);
        return next;
      });
      setBrowseFinalStates((prev) => {
        if (!prev.has(gameId)) return prev;
        const next = new Map(prev);
        next.delete(gameId);
        return next;
      });
      setBrowseReadyIds((prev) => new Set(prev).add(gameId));
      if (notify) {
        notifySubscribers(gameId, {
          prevState: m.board,
          curState: m.board,
          move: null,
          terminal: true,
          reason: "won",
          wonElapsedMs,
        });
      }
      // Floor of a few seconds: even a joiner arriving at the very end
      // of the window gets the final-board settle, never a stuck slot.
      const remaining = Math.max(5000, CASCADE_GRACE_MS - wonElapsedMs);
      celebrationsRef.current.set(gameId, {
        timer: setTimeout(() => settleBrowseCelebration(gameId), remaining),
        wonAt: Date.now() - wonElapsedMs,
      });
    };

    // Scrolling away silently drops browse-only machines. This is the
    // client-memory half of the bounded protocol; watched desktop games
    // survive even when they leave the browser viewport.
    pruneBrowseRef.current = (gameIds) => {
      for (const gameId of gameIds) {
        if (seatPhasesRef.current.has(gameId)) continue;
        if (celebrationsRef.current.has(gameId)) {
          settleBrowseCelebration(gameId);
        } else {
          endGame(gameId);
        }
      }
      publishActiveGameIds();
    };

    // Apply one spectated move by wire index. Returns false on a bad
    // index — which the seq-guarded protocol makes a genuine violation,
    // so we ask for a fresh snapshot rather than freezing forever.
    const applySpectatedMove = (gameId, moveIndex) => {
      const m = machinesRef.current.get(gameId);
      const applied = machineApply(m, moveIndex);
      if (!applied) {
        forceResync(`bad move index ${moveIndex} for game ${gameId}`);
        return false;
      }
      machinesRef.current.set(gameId, applied.machine);
      notifySubscribers(gameId, {
        prevState: m.board,
        curState: applied.machine.board,
        move: applied.move,
        terminal: false,
      });
      return true;
    };

    // Register a freshly (re)built game machine from a self-contained
    // server snapshot: history + whether undo is armed. Callers notify
    // subscribers as appropriate.
    const setGameSnapshot = (gameId, moves, canUndo) => {
      // A snapshot for an id with no current machine is a NEW identity —
      // either first sight or a rebuild after endGame (e.g. Watch on a
      // ghost replay reusing a frozen window's number). The generation
      // feeds SpectatedSolitaire's re-arm effect: a frozen (terminal)
      // window instance must never silently adopt a different game.
      if (!machinesRef.current.has(gameId)) {
        machineGenRef.current.set(
          gameId,
          (machineGenRef.current.get(gameId) ?? 0) + 1,
        );
      }
      machinesRef.current.set(
        gameId,
        machineFromSnapshot(gameId, moves ?? [], !!canUndo, {
          label: "useSolitaireServer",
        }),
      );
      seqRef.current.set(gameId, (moves ?? []).length);
    };

    const onMessage = (evt) => {
      let parsed;
      try {
        parsed = JSON.parse(evt.data);
      } catch {
        return;
      }
      // The write pump coalesces queued frames into one message (a JSON
      // array in queue order). Unwrapping here keeps the FIFO guarantee:
      // a batch [a,b] is exactly "a then b" (see server/PROTOCOL.md).
      if (Array.isArray(parsed)) {
        for (const m of parsed) handleServerMsg(m);
      } else {
        handleServerMsg(parsed);
      }
    };

    const handleServerMsg = (msg) => {
      // Compact cursor relay ({"t":"c","g":id,"c":[x,y,down01]}) —
      // presence only, display-only, never engine state.
      if (msg.t === "c") {
        if (msg.g !== myGameIdRef.current && Array.isArray(msg.c)) {
          notifySubscribers(msg.g, {
            cursor: { x: msg.c[0], y: msg.c[1], down: msg.c[2] === 1 },
          });
        }
        return;
      }

      switch (msg.type) {
        case "welcome": {
          // The hello this answers is no longer pending — without this,
          // the error handler below would treat any later "ended" error
          // as a response to a request we never made.
          pendingGameIdRef.current = null;
          helloInFlightRef.current = false;
          hadWelcomeRef.current = true;
          // Dev knob: `?devwin=ID` asks the server (dev-create gate) to
          // force-end that live game as won, once, so its cascade can
          // be verified from this tab's neighbor windows / browse grid.
          if (!devWinSentRef.current) {
            devWinSentRef.current = true;
            const raw = new URLSearchParams(window.location.search).get(
              "devwin",
            );
            const target = raw ? parseInt(raw, 10) : NaN;
            if (Number.isFinite(target) && target > 0) {
              ws.send(
                JSON.stringify({ type: "dev_win", game_id: target }),
              );
            }
          }
          const wasSameGame = myGameIdRef.current === msg.your_game_id;
          updateMyGameId(msg.your_game_id);
          updateTimedOut(false);
          updateWon(false);
          setIdlePrompt(null);
          // Mirror the server's idle clock instead of resetting our own:
          // a reconnect blip must not suppress the "still there?" prompt
          // while the server's sweep marches on regardless.
          lastActivityRef.current =
            Date.now() - (typeof msg.idle_ms === "number" ? msg.idle_ms : 0);
          if (typeof msg.room_active === "number") {
            setRoomActive(msg.room_active);
          }
          if (typeof msg.neighbor_count === "number") {
            sentNeighborCountRef.current = msg.neighbor_count;
          }

          const freshSocket = freshSocketRef.current;
          freshSocketRef.current = false;
          // Reconnect = a fresh socket AFTER the session's first
          // welcome. Only reconnect arrivals open quietly.
          const reconnectWelcome = freshSocket && everWelcomedRef.current;
          everWelcomedRef.current = true;
          let serverMoves = [];
          let serverCanUndo = false;
          const incoming = new Map();
          for (const g of msg.active_games || []) {
            if (g.game_id === msg.your_game_id) {
              serverMoves = g.moves || [];
              serverCanUndo = !!g.can_undo;
            } else {
              incoming.set(g.game_id, g);
            }
          }
          // Always present (sendWelcome unconditionally includes it);
          // the ordered list is the authoritative desktop-set diff.
          const watchedIds = (msg.watched_game_ids ?? []).filter(
            Number.isInteger,
          );
          // Rebuild the seat map on the server's ordered list. Seats
          // already mid-ceremony keep their phase: a welcome for one
          // seat's reseat must not reanimate another seat's departure.
          const prevSeats = seatPhasesRef.current;
          seatPhasesRef.current = new Map(
            watchedIds.map((id) => [id, prevSeats.get(id) ?? "live"]),
          );

          // The watched ID list is authoritative and includes ended games,
          // whose final machines remain local even though active_games no
          // longer carries a live snapshot for them.
          for (const oldId of Array.from(machinesRef.current.keys())) {
            const stillBrowsed =
              browseWantedRef.current && browseRequestedRef.current.has(oldId);
            if (!seatPhasesRef.current.has(oldId) && !stillBrowsed) {
              endGame(oldId);
            }
          }

          // Games still/newly present: rebuild from the snapshot, and
          // resync existing subscribers by firing a non-terminal event
          // with the new state. Windows first seen on a RECONNECT
          // welcome open with zero animation — waking must present the
          // already-settled room, so arrivals get no theater either.
          for (const [gid, g] of incoming) {
            const prev = machinesRef.current.get(gid);
            if (reconnectWelcome && !prev) {
              quietArrivalIdsRef.current.add(gid);
            }
            setGameSnapshot(gid, g.moves, g.can_undo);
            if (prev) {
              notifySubscribers(gid, {
                prevState: prev.board,
                curState: machinesRef.current.get(gid).board,
                move: null,
                terminal: false,
              });
            }
          }
          publishActiveGameIds();

          // A reconnect creates a fresh server-side Client, so restore
          // the lightweight browser roster if its window stayed open.
          // Its visible-range effect follows with the bounded browse_set.
          // Gated on a genuinely fresh socket: reseat and watch welcomes
          // arrive mid-session and must leave the roster (ended stubs,
          // scroll position) alone.
          if (
            freshSocket &&
            browseWantedRef.current &&
            ws.readyState === ReconnectingWebSocket.OPEN
          ) {
            setBrowseGameIds([]);
            setBrowseReadyIds(new Set());
            browseRequestedRef.current = new Set();
            ws.send(JSON.stringify({ type: "browse_open" }));
          }

          // Dev knobs: stage a fake neighbor ending once per page load.
          if (
            spectateKnob &&
            !knobFiredRef.current &&
            machinesRef.current.size > 0
          ) {
            knobFiredRef.current = true;
            // Pick a game somebody is actually WATCHING (a machine
            // without subscribers has no window — its ending would play
            // to an empty room). Windows mount over several seconds
            // (staggered reveal), so retry until one is subscribed.
            const tryFire = (attemptsLeft) => {
              const id = [...subscribersRef.current.keys()].find((g) =>
                machinesRef.current.has(g),
              );
              if (id != null) finishWatchedGame(id, spectateKnob);
              else if (attemptsLeft > 0)
                setTimeout(() => tryFire(attemptsLeft - 1), 1000);
            };
            setTimeout(() => tryFire(20), 6000);
          }

          // Welcome-time consistency check: the server's history plus our
          // queued outbox must land exactly on our optimistic length, with
          // each queued op minted at the position it will arrive at. Then
          // (and only then) the outbox flushes. Anything else — including
          // an outbox containing an undo — drops the queue and rebuilds
          // from the server's history. (A queued move+undo pair nets out
          // to the server's history anyway, so rebuilding loses nothing.)
          const outbox = outboxRef.current;
          outboxRef.current = [];
          const queued = outbox.filter((e) => e.gameId === msg.your_game_id);
          const local = ownSeqRef.current;
          const contiguous = queued.every(
            (e, i) =>
              e.payload.type === "move" &&
              e.payload.seq === serverMoves.length + i,
          );
          const adopt = (rebuild) => {
            setMyGameMoves(serverMoves);
            setMyGameCanUndo(serverCanUndo);
            ownSeqRef.current = serverMoves.length;
            if (rebuild) setMyGameResyncNonce((n) => n + 1);
          };
          if (!wasSameGame || local == null) {
            adopt(false);
          } else if (
            local === serverMoves.length + queued.length &&
            contiguous
          ) {
            for (const e of queued) ws.send(JSON.stringify(e.payload));
          } else {
            console.warn(
              `own-game divergence at welcome (local ${local}, server ` +
                `${serverMoves.length}, queued ${queued.length}); rebuilding`,
            );
            adopt(true);
          }
          break;
        }

        case "browse_roster": {
          if (!browseWantedRef.current) break;
          if (browseRetryTimerRef.current != null) {
            clearTimeout(browseRetryTimerRef.current);
            browseRetryTimerRef.current = null;
          }
          const seen = new Set();
          const ids = [];
          for (const raw of msg.game_ids || []) {
            const id = Number(raw);
            if (!Number.isInteger(id) || seen.has(id)) continue;
            seen.add(id);
            ids.push(id);
          }
          setBrowseGameIds(ids);
          setBrowseEndedIds(new Set());
          setBrowseReadyIds(new Set());
          setBrowseFinalStates(new Map());
          browseRequestedRef.current = new Set();
          // Stale prune timers must not fire into the fresh roster —
          // an id present again here is a new live incarnation.
          for (const t of browsePruneTimersRef.current.values()) {
            clearTimeout(t);
          }
          browsePruneTimersRef.current.clear();
          break;
        }

        case "browse_retry": {
          if (!browseWantedRef.current) break;
          const operation = msg.operation;
          const seq = Number(msg.browse_seq);
          if (
            operation === "set" &&
            (!Number.isInteger(seq) || seq !== browseSeqRef.current)
          ) {
            break;
          }
          if (operation !== "open" && operation !== "set") break;
          if (browseRetryTimerRef.current != null) {
            clearTimeout(browseRetryTimerRef.current);
          }
          const retryMs = Math.max(
            250,
            Math.min(1000, Number(msg.retry_ms) || 300),
          );
          browseRetryTimerRef.current = setTimeout(() => {
            browseRetryTimerRef.current = null;
            if (
              !browseWantedRef.current ||
              ws.readyState !== ReconnectingWebSocket.OPEN
            ) {
              return;
            }
            if (operation === "open") {
              ws.send(JSON.stringify({ type: "browse_open" }));
            } else if (seq === browseSeqRef.current) {
              ws.send(
                JSON.stringify({
                  type: "browse_set",
                  game_ids: Array.from(browseRequestedRef.current),
                  browse_seq: seq,
                }),
              );
            }
          }, retryMs);
          break;
        }

        case "browse_added": {
          if (!browseWantedRef.current) break;
          const id = Number(msg.game_id);
          if (!Number.isInteger(id)) break;
          setBrowseGameIds((prev) =>
            prev.includes(id) ? prev : [...prev, id],
          );
          // A re-added ID means a ghost replay reused a historical number:
          // the old instance's ended stub and cached final board are stale
          // for the new one — clear them so the slot reads live again
          // (including any pending prune of the dead incarnation).
          {
            const timers = browsePruneTimersRef.current;
            clearTimeout(timers.get(id));
            timers.delete(id);
          }
          setBrowseEndedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setBrowseFinalStates((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Map(prev);
            next.delete(id);
            return next;
          });
          break;
        }

        case "browse_ended": {
          if (!browseWantedRef.current) break;
          const id = Number(msg.game_id);
          if (!Number.isInteger(id)) break;
          // Mid-cascade: the reasoned game_ended arrived in the same
          // server beat and deferred this bookkeeping to the settle.
          if (celebrationsRef.current.has(id)) break;
          setBrowseEndedIds((prev) => new Set(prev).add(id));
          rememberBrowseFinal(id);
          setBrowseReadyIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          scheduleBrowsePrune(id);
          break;
        }

        case "game_present": {
          if (msg.game_id === myGameIdRef.current) break;
          // A scroll may have moved again while this snapshot was in
          // flight. Only the exact current generation may make a browse
          // slot live; ordinary watched-game snapshots have no marker.
          if (
            msg.browse &&
            (msg.browse_seq !== browseSeqRef.current ||
              !browseRequestedRef.current.has(msg.game_id))
          ) {
            break;
          }
          // A game that WON inside its celebration window is still a
          // roster entry (server/PROTOCOL.md §6): the ended stub carries
          // won_elapsed_ms, and the cascade is deterministic per game id,
          // so this slot can join the show in progress — build the (won)
          // machine, keep the slot live, and let SpectatedSolitaire
          // fast-forward the sim by the elapsed time.
          if (
            msg.browse &&
            msg.ended &&
            msg.reason === "won" &&
            typeof msg.won_elapsed_ms === "number"
          ) {
            if (celebrationsRef.current.has(msg.game_id)) {
              // Already celebrating here (range churn re-requests the id
              // because the slot never reads ended mid-show): keep the
              // running show, just re-mark the slot ready.
              setBrowseReadyIds((prev) => new Set(prev).add(msg.game_id));
              break;
            }
            setGameSnapshot(msg.game_id, msg.moves, false);
            beginBrowseCelebration(msg.game_id, {
              notify: true,
              wonElapsedMs: Math.max(0, msg.won_elapsed_ms),
            });
            publishActiveGameIds();
            break;
          }
          // The celebration join above is the ONLY ended snapshot the
          // wire carries; plain ended stubs fetch their final board over
          // HTTP (loadBrowseFinal). Anything else ended here is stray.
          if (msg.ended) break;
          // Server snapshots are authoritative: always adopt, replacing
          // any machine we already had (this is also the resync path).
          const prev = machinesRef.current.get(msg.game_id);
          setGameSnapshot(msg.game_id, msg.moves, msg.can_undo);
          if (prev) {
            notifySubscribers(msg.game_id, {
              prevState: prev.board,
              curState: machinesRef.current.get(msg.game_id).board,
              move: null,
              terminal: false,
            });
          }
          if (!msg.browse && !seatPhasesRef.current.has(msg.game_id)) {
            seatPhasesRef.current.set(msg.game_id, "live");
          }
          publishActiveGameIds();
          if (browseRequestedRef.current.has(msg.game_id)) {
            setBrowseReadyIds((prev) => new Set(prev).add(msg.game_id));
          }
          break;
        }

        case "move": {
          if (msg.game_id === myGameIdRef.current) break;
          // A move that completed a drag carries its cursor release
          // edge (c:[x,y,0]). Dispatch the release FIRST, in the same
          // task as the move below — the spectated window restores its
          // drag ghost and applies the board change in one React
          // commit, which is exactly what makes the drop flicker-free.
          if (Array.isArray(msg.c) && msg.c.length === 3) {
            notifySubscribers(msg.game_id, {
              cursor: { x: msg.c[0], y: msg.c[1], down: msg.c[2] === 1 },
            });
          }
          if (!machinesRef.current.has(msg.game_id)) {
            // A move for a game we no longer track: routine after a
            // browse scroll-away prune (frames for the old range are
            // still in flight). Drop it.
            break;
          }
          const nextSeq = seqRef.current.get(msg.game_id) ?? 0;
          if (msg.seq < nextSeq) break; // duplicate/stale — already reduced
          if (msg.seq > nextSeq) {
            // A hole in a FIFO no-holes stream: protocol violation.
            forceResync(
              `seq gap for game ${msg.game_id} (${msg.seq} > ${nextSeq})`,
            );
            break;
          }
          if (applySpectatedMove(msg.game_id, msg.move)) {
            seqRef.current.set(msg.game_id, nextSeq + 1);
          }
          break;
        }

        case "room": {
          if (typeof msg.active === "number") setRoomActive(msg.active);
          break;
        }

        // A neighbor took back their last move. Machines are built from
        // self-contained snapshots (can_undo included), so a machine that
        // exists can always honor a server-approved undo — a null here is
        // a protocol violation, answered loudly with a resync request.
        case "undo": {
          if (msg.game_id === myGameIdRef.current) break; // own undo is local
          const m = machinesRef.current.get(msg.game_id);
          if (!m) break;
          const undone = machineUndo(m);
          if (!undone) {
            forceResync(`undo with no armed prevBoard for game ${msg.game_id}`);
            break;
          }
          if (typeof msg.seq === "number") {
            seqRef.current.set(msg.game_id, msg.seq);
          }
          machinesRef.current.set(msg.game_id, undone);
          notifySubscribers(msg.game_id, {
            prevState: m.board,
            curState: undone.board,
            move: null,
            terminal: false,
          });
          break;
        }

        case "game_ended": {
          if (msg.game_id !== myGameIdRef.current) {
            const watched = seatPhasesRef.current.has(msg.game_id);
            // A visible winner (watched or browse-only): defer the
            // browse bookkeeping so the grid slot plays the cascade
            // instead of snapping to a frozen "Ended" board. Watched
            // desktops still finish normally — their instance runs the
            // same show and keeps the final board after.
            const celebrate =
              msg.reason === "won" &&
              browseWantedRef.current &&
              machinesRef.current.has(msg.game_id);
            if (celebrate) {
              beginBrowseCelebration(msg.game_id, { notify: !watched });
            } else if (browseWantedRef.current) {
              setBrowseEndedIds((prev) => new Set(prev).add(msg.game_id));
              rememberBrowseFinal(msg.game_id);
            }
            if (watched) {
              finishWatchedGame(msg.game_id, msg.reason);
            } else if (!celebrate) {
              endGame(msg.game_id, msg.reason);
            }
            publishActiveGameIds();
            break;
          }
          endGame(msg.game_id, msg.reason);
          publishActiveGameIds();
          if (myGameIdRef.current === msg.game_id) {
            outboxRef.current = [];
            if (msg.reason === "won") {
              // Our own win claim echoing back. Keep the won board mounted
              // — nulling the ID here would unmount the app mid-cascade.
              // A fresh deal comes later via dealNewGame().
              updateWon(true);
              setIdlePrompt(null); // never ask "Still there?" over a win
              storeGameId(null); // but a refresh should start fresh
            } else if (!helloInFlightRef.current) {
              // Abandoned/timed out under us (idle sweep). Don't silently
              // deal a new game — keep the dead board mounted and let the
              // UI say what happened; the player chooses to re-deal. The
              // stored id is dead too: a refresh must not resurrect it.
              storeGameId(null);
              setIdlePrompt(null);
              updateTimedOut(true);
            }
          }
          break;
        }

        // Our optimistic local state diverged from the server's (stale
        // seq, illegal index, rate limit). Adopt the server's history and
        // force PlayableSolitaire to rebuild from it.
        case "move_rejected": {
          if (msg.game_id !== myGameIdRef.current) break;
          console.warn("server rejected a move; resyncing own game");
          outboxRef.current = [];
          setMyGameMoves(msg.moves || []);
          setMyGameCanUndo(!!msg.can_undo);
          ownSeqRef.current = (msg.moves || []).length;
          setMyGameResyncNonce((n) => n + 1);
          break;
        }

        case "error":
          // Structured codes, not prose: the error text is for humans.
          if (
            msg.code === "game_unavailable" &&
            pendingGameIdRef.current != null
          ) {
            // Our stored game_id names a game that's gone (ended, timed
            // out during an outage, or not ours). Drop it and hello
            // fresh for a new deal.
            storeGameId(null);
            pendingGameIdRef.current = null;
            outboxRef.current = [];
            sendHello(null);
          } else if (msg.code === "hello_throttled") {
            // The hello bucket is empty (reconnect storm). Retry the
            // SAME hello after the bucket refills — without this a
            // throttled resume stranded the client gameless forever.
            helloInFlightRef.current = false;
            const retryId = pendingGameIdRef.current;
            setTimeout(() => {
              if (
                myGameIdRef.current == null &&
                ws.readyState === ReconnectingWebSocket.OPEN
              ) {
                sendHello(retryId);
              }
            }, 4500);
          } else {
            helloInFlightRef.current = false;
            console.warn("server error:", msg.error);
          }
          break;
        default:
          break;
      }
    };

    ws.addEventListener("open", onOpen);
    ws.addEventListener("close", onClose);
    ws.addEventListener("message", onMessage);

    // Wake-from-suspend nudge. Safari (especially iOS) aggressively
    // bfcaches and freezes tabs; a restored page can hold a zombie
    // socket — readyState still OPEN but the NAT/proxy path long dead,
    // and no close event ever fires, so partysocket never retries on
    // its own. Cycling the socket is the sanctioned recovery path: the
    // welcome flow re-asserts the room, and a live seat rebinds by id.
    let hiddenAt = null;
    const onPageShow = (e) => {
      if (e.persisted) ws.reconnect();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (hiddenAt != null) {
        // Long enough hidden that intermediaries may have dropped the
        // idle connection (Cloudflare cuts at ~100s; keepalive pings
        // are throttled in background tabs). Short blur/refocus flips
        // stay untouched.
        if (Date.now() - hiddenAt > 90 * 1000) ws.reconnect();
        hiddenAt = null;
      }
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
      ws.removeEventListener("open", onOpen);
      ws.removeEventListener("close", onClose);
      ws.removeEventListener("message", onMessage);
      ws.close();
      if (browseRetryTimerRef.current != null) {
        clearTimeout(browseRetryTimerRef.current);
        browseRetryTimerRef.current = null;
      }
      if (netTimerRef.current != null) {
        clearTimeout(netTimerRef.current);
        netTimerRef.current = null;
      }
      pruneBrowseRef.current = () => {};
      settleCelebrationRef.current = () => {};
      for (const t of browsePruneTimersRef.current.values()) clearTimeout(t);
      browsePruneTimersRef.current.clear();
      for (const show of celebrationsRef.current.values()) {
        clearTimeout(show.timer);
      }
      celebrationsRef.current.clear();
    };
  }, [updateMyGameId]);

  // The room layout is selected before the socket hook runs, so the first
  // hello already carries the right count (mobile sends zero). If compact
  // mode changes later, resize the watched-set ceiling without reconnecting.
  React.useEffect(() => {
    if (
      myGameId == null ||
      sentNeighborCountRef.current === boundedNeighborCount
    ) {
      return;
    }
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(
        JSON.stringify({
          type: "set_neighbor_count",
          neighbor_count: boundedNeighborCount,
        }),
      );
      sentNeighborCountRef.current = boundedNeighborCount;
    }
  }, [boundedNeighborCount, myGameId]);

  // Send now if the socket is open; otherwise queue against the current
  // game so a reconnect blip doesn't silently lose moves. The queue is
  // flushed after the reconnect welcome confirms the server's history
  // sits exactly underneath it (and dropped if it doesn't — see the
  // welcome handler's consistency check).
  const send = React.useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    } else if (myGameIdRef.current != null) {
      outboxRef.current.push({ gameId: myGameIdRef.current, payload });
    }
  }, []);

  // Volatile send for presence traffic: if the socket is down, drop it —
  // stale cursor positions must never queue behind real moves. Compact
  // form ({"t":"c","c":[x,y,down01]}) — the chattiest message on the
  // wire earns the short keys. `down` mirrors the held button so
  // watchers see clicks and drags.
  const sendCursor = React.useCallback((x, y, down = false) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify({ t: "c", c: [x, y, down ? 1 : 0] }));
    }
  }, []);

  // Every own-game op is minted at our current optimistic history length
  // (seq); the server accepts it only at exactly that position. See
  // ownSeqRef above.
  const sendMove = React.useCallback(
    // releaseAt: [x,y] when this move completed a drag — the cursor
    // release edge rides ON the move message so watchers get
    // release+move as one atomic frame (PROTOCOL.md §4).
    // `draw` mirrors the server's idle clock, which draws never feed: a
    // pure stock-cycler used to sail past the "no move" warning (client
    // counted draws, server didn't) straight into a timed-out notice.
    (moveIndex, releaseAt = null, { draw = false } = {}) => {
      if (!draw) lastActivityRef.current = Date.now();
      const seq = ownSeqRef.current ?? 0;
      ownSeqRef.current = seq + 1;
      // Mirror the op into the published own-history optimistically, so
      // initialMoves is NEVER stale: a PlayableSolitaire remount from
      // any cause rebuilds today's position instead of silently
      // rewinding to welcome-time state. A rejection overwrites this
      // with the server's truth.
      setMyGameMoves((prev) => (prev == null ? prev : [...prev, moveIndex]));
      setMyGameCanUndo(true);
      const msg = { type: "move", move: moveIndex, seq };
      if (releaseAt) msg.c = [Math.round(releaseAt[0]), Math.round(releaseAt[1]), 0];
      send(msg);
    },
    [send],
  );
  // Shut Down's honest exit: powering off gives the game up immediately
  // — you can't resume a computer you turned off. Best-effort
  // fire-and-forget on the live socket only; if the socket is down the
  // idle sweep ends the game soon anyway, and queuing an abandon behind
  // a reconnect could kill a game the player believes they resumed.
  const abandonGame = React.useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "abandon" }));
    }
  }, []);

  // Undoing a draw is still stock-cycling — the server's idle clock
  // ignores it, so ours must too (same mirror as sendMove).
  const sendUndo = React.useCallback(({ draw = false } = {}) => {
    if (!draw) lastActivityRef.current = Date.now();
    const seq = ownSeqRef.current ?? 0;
    ownSeqRef.current = Math.max(0, seq - 1);
    // Single-step undo: pop the mirrored op; the undo slot is spent.
    setMyGameMoves((prev) =>
      prev == null || prev.length === 0 ? prev : prev.slice(0, -1),
    );
    setMyGameCanUndo(false);
    send({ type: "undo", seq });
  }, [send]);

  // The one warning. Note the client counts draws as activity but the
  // server doesn't — a pure-stock-cycler can still be swept, and then
  // the timed-out notice covers it.
  React.useEffect(() => {
    const t = setInterval(() => {
      if (myGameIdRef.current == null) return;
      if (myGameTimedOutRef.current) return;
      // A won game is over — sitting with the cascade (or the finished
      // board) is not idling; there is nothing for the server to sweep.
      if (myGameWonRef.current) return;
      const gameIdle = Date.now() - lastActivityRef.current;
      const inputQuiet = Date.now() - lastInputRef.current;
      // Under the screensaver (or headed there): no prompt. The game
      // dies behind the pipes and the timed-out notice waits for wake.
      if (inputQuiet >= AWAKE_MS) return;
      if (gameIdle > SERVER_IDLE_MS - LAST_CHANCE_LEAD_MS) {
        // Sweep imminent and they're demonstrably present: warn, and
        // say why ("no move", not "no sign of you").
        const next = {
          kind: "no-move",
          minutes: Math.max(1, Math.round(gameIdle / 60000)),
        };
        setIdlePrompt((prev) =>
          prev && prev.kind === next.kind && prev.minutes === next.minutes
            ? prev
            : next,
        );
      }
      // An already-raised prompt never auto-dismisses (a mouse wiggle
      // silently swallowing a modal would be spookier than the modal).
    }, IDLE_CHECK_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmStillHere = React.useCallback(() => {
    // The server may have swept the game while the prompt sat on
    // screen (its idle clock ignores draws; ours doesn't). If so, the
    // timed-out notice has already replaced the prompt — a late OK
    // must not send still_here for a dead game.
    if (myGameTimedOutRef.current || myGameWonRef.current) return;
    lastActivityRef.current = Date.now();
    setIdlePrompt(null);
    // Volatile, like cursor traffic: presence must never queue in the
    // outbox (a stale still_here delivered after reconnect would lie
    // about the idle clock; the welcome's idle_ms re-derives the honest
    // state, and the prompt re-raises if it's still true).
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "still_here" }));
    }
  }, []);

  const dismissNetTrouble = React.useCallback(() => setNetTrouble(false), []);
  // Browser control is volatile UI state, never part of the own-game
  // outbox. Reconnect restoration happens from browseWantedRef in the
  // welcome handler above.
  const sendBrowse = React.useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    }
  }, []);
  const openGameBrowser = React.useCallback(() => {
    if (browseRetryTimerRef.current != null) {
      clearTimeout(browseRetryTimerRef.current);
      browseRetryTimerRef.current = null;
    }
    browseWantedRef.current = true;
    setBrowseGameIds([]);
    setBrowseEndedIds(new Set());
    setBrowseReadyIds(new Set());
    setBrowseFinalStates(new Map());
    browseRequestedRef.current = new Set();
    for (const t of browsePruneTimersRef.current.values()) clearTimeout(t);
    browsePruneTimersRef.current.clear();
    sendBrowse({ type: "browse_open" });
  }, [sendBrowse]);
  const setBrowsedGames = React.useCallback(
    (gameIds) => {
      if (browseRetryTimerRef.current != null) {
        clearTimeout(browseRetryTimerRef.current);
        browseRetryTimerRef.current = null;
      }
      const next = new Set(gameIds);
      const previous = browseRequestedRef.current;
      browseRequestedRef.current = next;
      const leaving = [];
      for (const id of previous) {
        if (!next.has(id)) leaving.push(id);
      }
      if (leaving.length > 0) pruneBrowseRef.current(leaving);
      setBrowseReadyIds((ready) => {
        const kept = new Set();
        for (const id of ready) {
          if (next.has(id) && previous.has(id)) kept.add(id);
        }
        return kept;
      });
      browseSeqRef.current += 1;
      sendBrowse({
        type: "browse_set",
        game_ids: gameIds,
        browse_seq: browseSeqRef.current,
      });
    },
    [sendBrowse],
  );
  // Fetch an ended stub's final board from the Past Games endpoint.
  // A just-ended game persists asynchronously, so a 404 retries a few
  // times before giving up (the stub simply stays a labeled empty board
  // and a later scroll retries). Celebrating wins never come through
  // here — their wire snapshot carries won_elapsed_ms.
  const loadBrowseFinal = React.useCallback((gameId) => {
    if (finalFetchesRef.current.has(gameId)) return;
    finalFetchesRef.current.add(gameId);
    const attempt = (triesLeft) => {
      fetchFinalBoard(gameId)
        .then((board) => {
          finalFetchesRef.current.delete(gameId);
          if (board) rememberFinalRef.current(gameId, board);
        })
        .catch(() => {
          if (triesLeft > 0) {
            setTimeout(() => attempt(triesLeft - 1), 900);
          } else {
            finalFetchesRef.current.delete(gameId);
          }
        });
    };
    attempt(3);
  }, []);

  const closeGameBrowser = React.useCallback(() => {
    if (browseRetryTimerRef.current != null) {
      clearTimeout(browseRetryTimerRef.current);
      browseRetryTimerRef.current = null;
    }
    browseWantedRef.current = false;
    setBrowseGameIds([]);
    setBrowseEndedIds(new Set());
    setBrowseReadyIds(new Set());
    setBrowseFinalStates(new Map());
    browseRequestedRef.current = new Set();
    for (const t of browsePruneTimersRef.current.values()) clearTimeout(t);
    browsePruneTimersRef.current.clear();
    sendBrowse({ type: "browse_close" });
  }, [sendBrowse]);
  // Put one specific live game into the watched desktop set. The server
  // answers with a welcome containing the complete ordered set.
  const watchGame = React.useCallback((gameId) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "watch", game_id: gameId }));
    }
  }, []);
  const unwatchGame = React.useCallback((gameId) => {
    // Optimistic local removal: X must stick even across a socket blip.
    // If the unwatch frame is lost, the reconnect hello simply re-asserts
    // a watched set without this id — no haunted seat either way.
    seatPhasesRef.current.delete(gameId);
    setWatchedGameIds(Array.from(seatPhasesRef.current.keys()));
    const ws = wsRef.current;
    if (ws && ws.readyState === ReconnectingWebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "unwatch", game_id: gameId }));
    }
  }, []);

  // Give up the current game (recorded as an abandon — losses are part of
  // the piece) and ask for a fresh one, as a single server-side op:
  // hello {fresh} abandons whatever this session owns and deals anew.
  // Used by the Start menu's "Deal New Game".
  const dealNewGame = React.useCallback(() => {
    outboxRef.current = [];
    updateTimedOut(false);
    setIdlePrompt(null);
    lastActivityRef.current = Date.now();
    storeGameId(null);
    if (sendHelloRef.current) sendHelloRef.current(null, { fresh: true });
  }, []);

  // A watched window finished its departure ceremony (close ritual /
  // cascade). Hide it from the desktop; the id stays in the watched set
  // so a reconnect re-assert keeps the seat until the server's reseat
  // welcome swaps in the replacement (which clears everything via the
  // normal welcome diff).
  const departWatchedGame = React.useCallback((gameId) => {
    const phase = seatPhasesRef.current.get(gameId);
    if (phase !== "ended" && phase !== "departed") return;
    seatPhasesRef.current.set(gameId, "departed");
    setDepartedWatchedIds((prev) => new Set(prev).add(gameId));
  }, []);

  // Subscribe to a spectated game's move stream. Returns a handle whose
  // `state` is the current board (use it for immediate render);
  // subsequent moves arrive via onEvent. On terminal the subscriber is
  // auto-unsubscribed. Stable across renders.
  const subscribeGame = React.useCallback((gameId, onEvent) => {
    const machine = machinesRef.current.get(gameId);
    let set = subscribersRef.current.get(gameId);
    if (!set) {
      set = new Set();
      subscribersRef.current.set(gameId, set);
    }
    set.add(onEvent);
    const wonAt = celebrationsRef.current.get(gameId)?.wonAt;
    return {
      state: machine ? machine.board : undefined,
      // Non-null when this game's win cascade is mid-flight: how far
      // the show has run. A window mounting now (a browse slot opened
      // mid-show) starts its celebration fast-forwarded by this much
      // instead of waiting for a terminal event that already fired.
      celebrationElapsedMs: wonAt != null ? Math.max(0, Date.now() - wonAt) : null,
      unsubscribe: () => {
        const s = subscribersRef.current.get(gameId);
        if (!s) return;
        s.delete(onEvent);
        if (s.size === 0) subscribersRef.current.delete(gameId);
      },
      // The window's win cascade finished playing. Only meaningful for
      // browse-grid celebrations (deferred teardown lands now); no-op
      // for everything else.
      cascadeDone: () => settleCelebrationRef.current(gameId),
    };
  }, []);

  return {
    myGameId,
    myGameMoves,
    myGameCanUndo,
    myGameResyncNonce,
    idlePrompt,
    confirmStillHere,
    myGameTimedOut,
    myGameWon,
    netTrouble,
    dismissNetTrouble,
    activeGameIds,
    watchedGameIds,
    departedWatchedIds,
    departWatchedGame,
    quietArrivalIds,
    gameGenerations,
    browseGameIds,
    browseEndedIds,
    browseReadyIds,
    browseFinalStates,
    roomActive,
    openGameBrowser,
    setBrowsedGames,
    loadBrowseFinal,
    closeGameBrowser,
    watchGame,
    unwatchGame,
    sendMove,
    sendUndo,
    sendCursor,
    dealNewGame,
    abandonGame,
    subscribeGame,
  };
}
